import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import MainLayout from '../components/Layout/MainLayout';
import ProtectedRoute from '../ProtectedRoute';
import ErrorBoundary from '../components/common/ErrorBoundary';
import Preloader from '../components/common/Preloader';
import ReferralRedirect from '../pages/ReferralRedirect.jsx';
import authService from '../services/authService.jsx';

// Lazy load layouts
const CustomerLayout = lazy(() => import('../pages/Customer/CustomerLayout'));
const ArtisanLayout = lazy(() => import('../pages/Artisan/ArtisanLayout'));

// Lazy load onboarding pages
const Login = lazy(() => import('../pages/Onboarding/Login'));
const Signup = lazy(() => import('../pages/Onboarding/Signup'));
const ForgotPassword = lazy(() => import('../pages/Onboarding/ForgotPassword'));
const VerificationCode = lazy(() => import('../pages/Onboarding/VerificationCode'));
const TwoFactorAuth = lazy(() => import('../pages/Onboarding/TwoFactorAuth'));
const CreateNewPassword = lazy(() => import('../pages/Onboarding/CreateNewPassword'));

// Lazy load customer pages
const CustomerDashboard = lazy(() => import('../pages/Customer/Dashboard/Dashboard'));
const CustomerFindArtisans = lazy(() => import('../pages/Customer/FindArtisans/FindArtisans'));
const CustomerMyJobs = lazy(() => import('../pages/Customer/MyJobs/CustomerMyJobs'));
const CustomerMessages = lazy(() => import('../pages/Customer/Messages/Messages'));
const CustomerProfileSettings = lazy(() => import('../pages/Customer/ProfileSettings/ProfileSettings'));
const CustomerPostJobLayout = lazy(() => import('../pages/Customer/PostJob/PostJobLayout'));
const CustomerDescribeJob = lazy(() => import('../pages/Customer/PostJob/DescribeJob'));
const CustomerTimeLocation = lazy(() => import('../pages/Customer/PostJob/TimeLocation'));
const CustomerReviewPost = lazy(() => import('../pages/Customer/PostJob/ReviewPost'));
const CustomerHireArtisanLayout = lazy(() => import('../pages/Customer/FindArtisans/HireArtisan/HireArtisanLayout'));
const CustomerHireArtisanDescribeJob = lazy(() => import('../pages/Customer/FindArtisans/HireArtisan/DescribeJob'));
const CustomerHireArtisanTimeLocation = lazy(() => import('../pages/Customer/FindArtisans/HireArtisan/TimeLocation'));
const CustomerHireArtisanReviewSubmit = lazy(() => import('../pages/Customer/FindArtisans/HireArtisan/ReviewSubmit'));
const CustomerArtisanDetails = lazy(() => import('../pages/Customer/FindArtisans/ArtisanDetails'));
const CustomerJobDetails = lazy(() => import('../pages/Customer/MyJobs/CustomerMyJobDetails'));
const PostedJobDetails = lazy(() => import('../pages/Customer/MyJobs/PostedJobDetails'));
const UserConsultationsPage = lazy(() => import('../pages/consultations/UserConsultationsPage'));
const ConsultationDetailsPage = lazy(() => import('../pages/consultations/ConsultationDetailsPage'));
const SupportTicketsPage = lazy(() => import('../pages/support/SupportTicketsPage'));

// Lazy load artisan pages
const ArtisanDashboard = lazy(() => import('../pages/Artisan/Dashboard/Dashboard'));
const ArtisanProfileSettings = lazy(() => import('../pages/Artisan/ProfileSettings/ProfileSettings'));
const ArtisanMyJobs = lazy(() => import('../pages/Artisan/MyJobs/ArtisanMyJobs'));
const ArtisanMyJobDetails = lazy(() => import('../pages/Artisan/MyJobs/ArtisanMyJobDetails.jsx'));
const ArtisanMessages = lazy(() => import('../pages/Artisan/Messages/Messages'));
const ArtisanFindJob = lazy(() => import('../pages/Artisan/FindJob/FindJob'));
const ArtisanJobDetails = lazy(() => import('../pages/Artisan/FindJob/ArtisanJobDetails'));
const ArtisanProposalsPage = lazy(() => import('../pages/Artisan/FindJob/ArtisanProposalsPage'));
const ArtisanNegotiationsPage = lazy(() => import('../pages/Artisan/FindJob/ArtisanNegotiationsPage'));
const ArtisanProposalDetails = lazy(() => import('../pages/Artisan/FindJob/ArtisanProposalDetails'));
const ArtisanConsultationsPage = lazy(() => import('../pages/consultations/ArtisanConsultationsPage'));

// Lazy load other pages
const PaymentCallback = lazy(() => import('../pages/Payment/PaymentCallback'));
const NotificationsModal = lazy(() => import('../components/Notifications/NotificationsModal'));
const NotFound = lazy(() => import('../pages/NotFound'));
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('../pages/TermsOfService'));
const DeactivateAccount = lazy(() => import('../pages/DeactivateAccount'));

// Router component to select correct job details page
function CustomerJobDetailsRouter() {
  const { tab } = useParams();
  return tab === 'posted' ? <PostedJobDetails /> : <CustomerJobDetails />;
}

function ArtisanFindJobsLegacyRedirect() {
  const { tab, jobId } = useParams();

  if (tab === 'proposal-sent') {
    return <Navigate to={jobId ? `/artisan/proposals/${jobId}` : '/artisan/proposals'} replace />;
  }

  if (tab === 'negotiations') {
    return <Navigate to="/artisan/direct-hires" replace />;
  }

  if (tab === 'requests' || tab === 'listings') {
    return <Navigate to={jobId ? `/artisan/find-jobs/requests/${jobId}` : '/artisan/find-jobs'} replace />;
  }

  return <Navigate to="/artisan/find-jobs" replace />;
}

const ProtectedLayout = ({ requiredRole = null, children }) => (
  <ProtectedRoute requiredRole={requiredRole}>
    <MainLayout>{children}</MainLayout>
  </ProtectedRoute>
);

const getRoleDashboardPath = (role) => {
  return role === 'Artisan' ? '/artisan/dashboard/in-progress' : '/customer/dashboard/posted';
};

const AuthPageRoute = ({ children, allowIncompleteOnboarding = false }) => {
  const onboardingState = authService.getRequiredOnboardingState();

  if (authService.isAuthenticated()) {
    if (onboardingState) {
      return allowIncompleteOnboarding ? children : <Navigate to="/signup" state={onboardingState} replace />;
    }

    return <Navigate to={getRoleDashboardPath(authService.getUserRole())} replace />;
  }

  return children;
};

const AppRoutes = () => {
  const location = useLocation();
  const backgroundLocation = location.state?.backgroundLocation || location;
  return (
    <>
      <ErrorBoundary>
        <Suspense fallback={<Preloader />}>
          <Routes location={backgroundLocation}>
            {/* Public Routes */}
            <Route
              path="/"
              element={
                <AuthPageRoute>
                  <Login />
                </AuthPageRoute>
              }
            />
            <Route
              path="/login"
              element={
                <AuthPageRoute>
                  <Login />
                </AuthPageRoute>
              }
            />
            <Route
              path="/signup"
              element={
                <AuthPageRoute allowIncompleteOnboarding>
                  <Signup />
                </AuthPageRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <AuthPageRoute>
                  <ForgotPassword />
                </AuthPageRoute>
              }
            />
            <Route
              path="/verify-code"
              element={
                <AuthPageRoute>
                  <VerificationCode />
                </AuthPageRoute>
              }
            />
            <Route
              path="/two-factor-auth"
              element={
                <AuthPageRoute>
                  <TwoFactorAuth />
                </AuthPageRoute>
              }
            />
            <Route
              path="/create-new-password"
              element={
                <AuthPageRoute>
                  <CreateNewPassword />
                </AuthPageRoute>
              }
            />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/r/:code" element={<ReferralRedirect />} />

            {/* Payment Callback Route */}
            <Route path="/payment/callback" element={<PaymentCallback />} />

            {/* Deactivate Account Route */}
            <Route path="/deactivate-account" element={<DeactivateAccount />} />

            {/* Protected Customer Routes */}
            <Route element={<ProtectedLayout requiredRole="User" />}>
              <Route path="/dashboard/consultations" element={<UserConsultationsPage />} />
              <Route path="/dashboard/consultations/:id" element={<ConsultationDetailsPage />} />
              <Route path="/customer" element={<CustomerLayout />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard">
                  <Route index element={<Navigate to="posted" replace />} />
                  <Route path=":tab" element={<CustomerDashboard />} />
                </Route>
                <Route path="find-artisans">
                  <Route index element={<Navigate to="nearby" replace />} />
                  <Route path=":tab" element={<CustomerFindArtisans />} />
                  <Route path=":tab/:artisanId" element={<CustomerArtisanDetails />} />
                </Route>
                {/* Nested hire-artisan routes within artisan details */}
                <Route path="hire-artisan/:tab/:artisanId" element={<CustomerHireArtisanLayout />}>
                  <Route index element={<Navigate to="describe" replace />} />
                  <Route path="describe" element={<CustomerHireArtisanDescribeJob />} />
                  <Route path="time-location" element={<CustomerHireArtisanTimeLocation />} />
                  <Route path="review" element={<CustomerHireArtisanReviewSubmit />} />
                </Route>
                <Route path="jobs">
                  <Route index element={<Navigate to="posted" replace />} />
                  <Route path=":tab" element={<CustomerMyJobs />} />
                  <Route path=":tab/:jobId" element={<CustomerJobDetailsRouter />} />
                </Route>
                <Route path="messages" element={<CustomerMessages />} />
                <Route path="consultations" element={<UserConsultationsPage />} />
                <Route path="consultations/:id" element={<ConsultationDetailsPage />} />
                <Route path="support" element={<SupportTicketsPage />} />
                <Route path="profile&settings">
                  <Route index element={<Navigate to="personalInfo" replace />} />
                  <Route path="personalInfo" element={<CustomerProfileSettings />} />
                  <Route path=":tab" element={<CustomerProfileSettings />} />
                  <Route path=":tab/edit" element={<CustomerProfileSettings />} />
                </Route>

                {/* Post a Job multi-step nested routes */}
                <Route path="post-job" element={<CustomerPostJobLayout />}>
                  <Route index element={<Navigate to="describe" replace />} />
                  <Route path="describe" element={<CustomerDescribeJob />} />
                  <Route path="time-location" element={<CustomerTimeLocation />} />
                  <Route path="review" element={<CustomerReviewPost />} />
                </Route>
              </Route>
            </Route>

            {/* Protected Artisan Routes */}
            <Route element={<ProtectedLayout requiredRole="Artisan" />}>
              <Route path="/artisan" element={<ArtisanLayout />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard">
                  <Route index element={<Navigate to="in-progress" replace />} />
                  <Route path=":tab" element={<ArtisanDashboard />} />
                </Route>
                <Route path="profile&settings">
                  <Route index element={<Navigate to="personalInfo" replace />} />
                  <Route path="personalInfo" element={<ArtisanProfileSettings />} />
                  <Route path=":tab" element={<ArtisanProfileSettings />} />
                  <Route path=":tab/edit" element={<ArtisanProfileSettings />} />
                </Route>
                <Route path="my-jobs">
                  <Route index element={<Navigate to="ongoing" replace />} />
                  <Route path=":tab" element={<ArtisanMyJobs />} />
                  <Route path=":tab/:jobId" element={<ArtisanMyJobDetails />} />
                </Route>
                <Route path="find-jobs">
                  <Route index element={<ArtisanFindJob />} />
                  <Route path="requests/:jobId" element={<ArtisanJobDetails />} />
                  <Route path=":tab" element={<ArtisanFindJobsLegacyRedirect />} />
                  <Route path=":tab/:jobId" element={<ArtisanFindJobsLegacyRedirect />} />
                </Route>
                <Route path="proposals" element={<ArtisanProposalsPage />} />
                <Route path="proposals/:jobId" element={<ArtisanProposalDetails />} />
                <Route path="direct-hires" element={<ArtisanNegotiationsPage />} />
                <Route path="consultations" element={<ArtisanConsultationsPage />} />
                <Route path="consultations/:id" element={<ConsultationDetailsPage />} />
                <Route path="support" element={<SupportTicketsPage />} />
                <Route path="messages" element={<ArtisanMessages />} />
              </Route>
            </Route>
            {/* 404 - Not Found */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          {/* Modal Routes - shown on top of the main UI when URL matches */}
          {location !== backgroundLocation && (
            <Routes>
              <Route path="/customer/notifications" element={<NotificationsModal />} />
              <Route path="/artisan/notifications" element={<NotificationsModal />} />
            </Routes>
          )}
        </Suspense>
      </ErrorBoundary>
    </>
  );
};

export default AppRoutes;
